import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiEye, FiEyeOff, FiHome } from 'react-icons/fi';
import './Act.css';
import ActDashboard from './ActDashboard';
import KitActivationFlow from './KitActivationFlow';
import logo from '../../assets/SZALW[1].png';

// Sub-components are defined here for clarity.
const ActivationChoice = ({ setView, setActivationUser }) => (
  <div className="SACT-form-inner SACT-activation-choice">
    <h3 className="SACT-choice-title">Let's get you started</h3>
    <p className="SACT-choice-subtitle">
    Have you registered a kit before?</p>
    <div className="SACT-choice-actions">
    <button 
            className="SACT-btn SACT-btn-login"

        onClick={() => {
          setActivationUser(null);
          setView('activationFlow');
        }}
      >
        I'm New to SZA Longevity
      </button>
      <button 
        className="SACT-btn SACT-btn-imnew"
        onClick={() => setView('loginForActivation')}
      >
        Sign In & Activate
      </button>
    
    </div>
     <button className="SACT-link-button  " onClick={() => setView('login')}>
     ← Back
    </button>
  </div>
);

const LoginForm = ({ onLogin, isForActivation, setView, email, onEmailChange, password, onPasswordChange, showPassword, onShowPasswordChange, isLoading, error }) => (
  <div className="SACT-form-inner">
    <img src={logo} alt="SZA Longevity Logo" className="SACT-logo-init" />
    <form onSubmit={onLogin} className="SACT-form">
    {isForActivation && <h3 className="SACT-flow-title" >Sign in to activate your kit.</h3>}
    {isForActivation && <h3 className="SACT-flow-subtitle" >Enter your credentials.</h3>}

      <div className="SACT-input-group">
        <input
          type="text"
          id="emailOrPhone"
          value={email}
          onChange={onEmailChange}
          placeholder=""
          autoComplete="off"
          required
        />
        <label htmlFor="emailOrPhone">Email or Phone</label>
      </div>
      <div className="SACT-input-group">
        <input
          type={showPassword ? 'text' : 'password'}
          id="password"
          value={password}
          onChange={onPasswordChange}
          placeholder=""
          autoComplete="new-password"
          required
        />
        <label htmlFor="password">Password</label>
        <button
          type="button"
          className="SACT-pw-toggle"
          onClick={onShowPasswordChange}
        >
          {showPassword ? <FiEye /> : <FiEyeOff />}
        </button>
      </div>
      {error && <div className="SACT-error-message">{error}</div>}
      <motion.button
        type="submit"
        className="SACT-btn SACT-btn-login"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={isLoading}
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </motion.button>
      <a href="#/" className="SACT-link">Forgot password</a>
    </form>
    {!isForActivation && (
      <div className="SACT-form-footer">
        <p className="SACT-register-link">
          New to SZA Longevity? <a href="#/" onClick={() => setView('activationChoice')}>Register Your Kit</a>
        </p>
      </div>
    )}
    {isForActivation && (
       <button className="SACT-link-button" onClick={() => setView('activationChoice')}>
          ← Back 
        </button>
    )}
  </div>
);

// The main component
const Act = () => {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [view, setView] = useState('home'); // Start with home/landing view

  // This is the single source of truth for the logged-in user object.
  const [currentUser, setCurrentUser] = useState(null);

  const setCookie = (name, value, days = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };
  
  const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  };

  useEffect(() => {
    const checkExistingAuth = () => {
      // Load the FULL user object from localStorage.
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        try {
          setCurrentUser(JSON.parse(storedUser));
        } catch (e) {
          console.error("Failed to parse stored user:", e);
          localStorage.removeItem('currentUser'); // Clear corrupted data.
        }
      }
      setIsCheckingAuth(false);
    };
    checkExistingAuth();
  }, []);

  const handleLogin = async (e, startActivation = false) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://service9004.szapfs.org/auth/sign-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: emailOrPhone,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Login failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Persist the full user object and set it in state.
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      setCookie('ACT-Cookie', data.token, 30);
      setCurrentUser(data.user);

      if (startActivation) {
        setView('activationFlow');
      }
    } catch (error) {
      setError(error.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLoginForActivation = (e) => {
    handleLogin(e, true);
  };

  const handleLogout = () => {
    deleteCookie('ACT-Cookie');
    // Clear the user object from both state and storage.
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    setEmailOrPhone('');
    setPassword('');
    setError('');
    setView('home');
  };
  
  if (isCheckingAuth) {
    return (
      <div className="SACT-wrapper">
        <div className="SACT-loading-container">
          <div className="SACT-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // The component's main gatekeeper now checks for a valid user object.
  if (currentUser) {
    return <ActDashboard user={currentUser} onLogout={handleLogout} />;
  }

  const renderContent = () => {
    switch (view) {
      case 'activationChoice':
        return <ActivationChoice setView={setView} setActivationUser={setCurrentUser} />;
      case 'activationFlow':
        return <KitActivationFlow user={currentUser} onComplete={() => {
            // After a new user registers, the simplest way to sync state is to reload.
            // This will trigger the useEffect to load the new user from localStorage.
            window.location.reload();
        }} />;
      case 'loginForActivation':
        return <LoginForm 
          onLogin={handleLoginForActivation} 
          isForActivation={true} 
          setView={setView}
          email={emailOrPhone}
          onEmailChange={(e) => setEmailOrPhone(e.target.value)}
          password={password}
          onPasswordChange={(e) => setPassword(e.target.value)}
          showPassword={showPassword}
          onShowPasswordChange={() => setShowPassword(!showPassword)}
          isLoading={isLoading}
          error={error}
        />;
      case 'home':
      case 'login':
      default:
        return <LoginForm 
          onLogin={handleLogin} 
          isForActivation={false}
          setView={setView}
          email={emailOrPhone}
          onEmailChange={(e) => setEmailOrPhone(e.target.value)}
          password={password}
          onPasswordChange={(e) => setPassword(e.target.value)}
          showPassword={showPassword}
          onShowPasswordChange={() => setShowPassword(!showPassword)}
          isLoading={isLoading}
          error={error}
        />;
    }
  };

  const isFormView = view !== 'home';

  return (
    <div className="SACT-wrapper">
      <motion.div 
        className={`SACT-container ${isFormView ? 'form-view-active' : ''}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
      >
        <div className="SACT-content-panel">
          <div className="SACT-content-inner">
            <img src={logo} alt="SZA Longevity Logo" className="SACT-logo SACT-mobile-logo" />
            <h2 className="SACT-headline">
              Your omics holds the answers to your health.
            </h2>
            <p className="SACT-subheadline">
              We're here to guide you.
            </p>
          </div>
          <div className="SACT-mobile-actions">
            <button className="SACT-btn SACT-btn-primary" onClick={() => setView('activationChoice')}>Register a kit</button>
            <button className="SACT-btn SACT-btn-secondary" onClick={() => setView('login')}>Login</button>
            <a 
  href="https://szalongevity.co.uk" 
  className="SACT-mobile-link" 
  target="_blank" 
  rel="noopener noreferrer"
>
  Visit szalongevity.co.uk
</a>          </div>
        </div>
        
        <div className="SACT-form-panel">
          {isFormView && (
            <button className="SACT-mobile-back" onClick={() => setView('home')}>
              <FiHome />
            </button>
          )}
          {renderContent()}
        </div>
      </motion.div>
    </div>
  );
};

export default Act;